import { DISPALY_REWARD_GLOBAL_LIMIT_TARGET_TYPE, DISPLAY_EARNING_RULE_GLOBAL_LIMIT_PERIOD, EARNING_RULE_GLOBAL_LIMIT_TARGET_TYPE, recordListUserInfo } from 'config/CustomEnums';
import {
  getStampRecord,
  getStampRecords,
} from '../services/StampRecordsApiHelper';
import {
  convertCursorToNumber,
  convertNumberToCursor,
  createAction,
  formatNumberWithCommas,
} from '../utils';
import { getDetailPageDisplayLastModifiedDate, getListDisplayLastModifiedDate } from '../utils/TimeFormatUtil';
import { createModel } from './BaseModel';
import { loading } from './LoadingUtil';
import { parseTransactionDisplayType } from './TransactionModel';

const eventType = {
  ADD_STAMP: 'Add Stamp',
  REMOVE_STAMP: 'Remove Stamp',
};
export const recordType = {
  CUSTOMER_EARN: 'Customer Earn',
  ADMIN_REMOVE: 'Admin Remove',
  ADMIN_ADD: 'Admin Add',
  MERCHANT_REMOVE: 'Merchant Remove',
  MERCHANT_ADD: 'Merchant Add',
  STAMP_EXPIRED: 'Stamp Expired',
};

function getInitState() {
  return {
    listDisplayFields: [
      { displayName: 'ID', fieldName: 'pk', linked: true },
      ...recordListUserInfo,
      { displayName: 'Event Type', fieldName: 'displayEventType' },
      {
        displayName: 'Quantity',
        fieldName: 'quantity',
        orderField: 'quantity',
      },
      { displayName: 'Remark', fieldName: 'remark' },
      {
        displayName: 'Purchase date',
        fieldName: 'listDisplayPurchaseDate',
        orderField: 'triggerTime',
      },
      {
        displayName: 'Creation date',
        fieldName: 'listDisplayCreationDate',
        orderField: 'creationDate',
      },
    ],
    maxQuantityValue: 0,
  };
}

const parseStampRecordLinkedTransaction = (transaction) => {
  if (!transaction) {
    return {};
  }
  const transactionType = transaction?.transactionType
  return {
    pk: transaction?.pk,
    transactionType,
    transactionDisplayType: parseTransactionDisplayType(transaction.transactionType),
    posInvoiceId: transaction?.posInvoiceId,
    invoiceId: transaction?.invoiceId,
    totalValue: transaction.totalValue,
    displayTotalValue: transaction.totalValue
      ? `HK$ ${formatNumberWithCommas(transaction.totalValue)}`
      : '',
    paidAmount: transaction.paidAmount,
    displayPaidAmount: transaction.paidAmount
      ? `HK$ ${formatNumberWithCommas(transaction.paidAmount)}`
      : '',
    transactionDetailDisplayDate: getDetailPageDisplayLastModifiedDate(
      transaction.date
    ),
  }
}

const parseStampReachLimitInfo = (reachLimitInfo) => {
  if (!reachLimitInfo) {
    return {}
  }
  const parsedReachLimitInfo = JSON.parse(reachLimitInfo) || {};
  let displayLimit = ""
  if (parsedReachLimitInfo?.stamps_reach_limit_target === EARNING_RULE_GLOBAL_LIMIT_TARGET_TYPE.perTransaction) {
    displayLimit = `${DISPALY_REWARD_GLOBAL_LIMIT_TARGET_TYPE?.[parsedReachLimitInfo?.stamps_reach_limit_target]} maximum reward limit`
  } else if (parsedReachLimitInfo?.stamps_reach_limit_target === EARNING_RULE_GLOBAL_LIMIT_TARGET_TYPE.stampCampaignMaxQtySlot){
    displayLimit = `Stamp Campaign Max Quantity Slot`
  } else {
    displayLimit = `${DISPLAY_EARNING_RULE_GLOBAL_LIMIT_PERIOD?.[parsedReachLimitInfo?.stamps_reach_limit_period] || ""} maximum reward limit`
  }
  return {
    displayLimit,
    displayMaximumRewardLimit: `${formatNumberWithCommas(parsedReachLimitInfo?.stamps_limit_value) || "-"} stamps`,
    displayAccumulatedReward: `${formatNumberWithCommas(parsedReachLimitInfo?.stamps_total_earned) || "-"} stamps`,
    displayOriginalReward: `${formatNumberWithCommas(parsedReachLimitInfo?.stamps_value) || "-"} stamps`,
    displayFinalReward: `${formatNumberWithCommas(parsedReachLimitInfo?.stamps_value_after_limit) || "-"} stamps`,
  }
}

function parseStampRecord(item) {
  const name = item.owner?.nickname
    ? item.owner.nickname
    : `${item.owner.firstName} ${item.owner.lastName}`;
  return {
    pk: item.pk,
    id: item.id,
    name: name,
    membershipId: item.owner?.membershipId,
    mobilePhoneNumber: item.owner?.mobilePhoneNumberSubscriberNumber
    ? `+${item.owner.mobilePhoneNumberCountryCode}\r\n${item.owner.mobilePhoneNumberSubscriberNumber}` : null,
    email: item.owner?.emailAddress,
    customerId: item.owner?.pk,
    customerIsReset: item.owner?.isReset,
    recordType: item.recordType,
    displayRecordType: recordType[item.recordType],
    eventType: item.eventType,
    displayEventType: eventType[item.eventType],
    quantity: item.stampQuantity === 0 ? "0" : item.stampQuantity,
    remark: item.remark,
    listDisplayCreationDate: getListDisplayLastModifiedDate(item.creationDate),
    detailDisplayCreationDate: getDetailPageDisplayLastModifiedDate(item.creationDate),
    listDisplayPurchaseDate: getListDisplayLastModifiedDate(item.triggerTime),
    detailDisplayPurchaseDate: getDetailPageDisplayLastModifiedDate(item.triggerTime),
    transaction: parseStampRecordLinkedTransaction(item.transaction),
    reachLimit: item.reachLimit,
    reachLimitInfo: parseStampReachLimitInfo(item.reachLimitInfo),
    displayAdministrator: item.administrator?.pk
    ? `[ID: ${item.administrator?.pk}] ${item.administrator?.username}`
    : item.administrator?.username,
  };
}

export default createModel({
  namespace: 'stampRecord',
  states: getInitState(),
  params: {
    objectKey: 'stampRecords',
    pkNode: 'StampRecordNode',
    detailAPI: getStampRecord,
    parseDetail: (data) => parseStampRecord(data?.stampRecord),
    initState: getInitState(),
  },
  reducers: {},
  effects: {
    getCurrentPageStampRecords: [
      function* ({ payload }, { call, put }) {
        const { page } = payload;
        let afterCursor = undefined;
        if (page > 1) {
          afterCursor = convertNumberToCursor((page - 1) * 20 - 1);
        }
        const serviceArgs = [getStampRecords, afterCursor, payload];
        function* onSuccess(data) {
          const stampRecordData = data.stampRecords?.edges;
          const pageInfo = data.stampRecords?.pageInfo;
          const totalCount = data.stampRecords.totalCount;
          const startCursor = convertCursorToNumber(pageInfo.startCursor);
          const endCursor = convertCursorToNumber(pageInfo.endCursor);
          const stampRecordDataList = stampRecordData.map((item) =>
            parseStampRecord(item.node),
          );
          yield put(
            createAction('updateState')({
              pagedList: stampRecordDataList,
              pageInfo: {
                startCursor: isNaN(startCursor) ? 0 : startCursor + 1,
                endCursor: isNaN(endCursor) ? 0 : endCursor + 1,
              },
              totalCount,
              totalPage: Math.ceil(totalCount / 20),
              maxQuantityValue: data.stampRecords.maxStampQuantity,
            }),
          );
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
  },
});
